/**
 * WebSocket Context for real-time notifications.
 *
 * This module provides a WebSocket connection to the Gateway for receiving
 * real-time updates about operations, PDF generation, Drive uploads, etc.
 *
 * Features:
 * - Auto-reconnect with exponential backoff
 * - Room subscription/unsubscription for targeted events
 * - Connection status indicator
 * - Event listener registration
 */
import {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
    useCallback,
    useMemo,
    type ReactNode,
} from "react";
import { useAuth } from "./AuthContext";

// WebSocket states
const WS_STATES = {
    CONNECTING: "connecting",
    CONNECTED: "connected",
    DISCONNECTED: "disconnected",
    RECONNECTING: "reconnecting",
} as const;

/** Current WebSocket connection phase: connecting, connected, disconnected or reconnecting. */
export type WsState = (typeof WS_STATES)[keyof typeof WS_STATES];

// Default configuration
const WS_CONFIG = {
    // In development, connect directly to gateway to avoid webpack-dev-server proxy issues
    // In production, use relative path
    url:
        process.env.NODE_ENV === "development"
            ? "ws://localhost:8000/api/realtime" // Direct to gateway
            : "/api/realtime",
    reconnectInterval: 1000, // Start with 1 second
    maxReconnectInterval: 30000, // Max 30 seconds
    reconnectDecay: 2, // Exponential backoff multiplier
    pingInterval: 30000, // Ping every 30 seconds
    // F3 fix: Maximum reconnection attempts before giving up
    maxReconnectAttempts: 20,
};

/** Any inbound or outbound WebSocket message — payload is free-form per `type`. */
export interface WsMessage {
    type?: string;
    action?: string;
    room?: string;
    [key: string]: unknown;
}

/** Listener callback for `addEventListener(type, ...)`. */
export type WsEventListener = (message: WsMessage) => void;

/** Realtime connection state and room/event helpers exposed by {@link useWebSocket}. */
export interface WebSocketContextValue {
    connectionState: WsState;
    isConnected: boolean;
    isReconnecting: boolean;
    lastError: string | null;
    /** Join a room (ref-counted); returns true when a subscribe message was sent. */
    subscribe: (room: string) => boolean;
    /** Leave a room (ref-counted); unsubscribes on the server when the last subscriber leaves. */
    unsubscribe: (room: string) => void;
    /** Register a listener for an event type ("*" for all); returns a cleanup function. */
    addEventListener: (eventType: string, callback: WsEventListener) => () => void;
    WS_STATES: typeof WS_STATES;
}

// Create context
const WebSocketContext = createContext<WebSocketContextValue | null>(null);

interface WebSocketProviderProps {
    children: ReactNode;
}

/**
 * WebSocket Provider component.
 *
 * Manages the WebSocket connection lifecycle and provides methods
 * for subscribing to rooms and listening to events.
 */
export const WebSocketProvider = ({ children }: WebSocketProviderProps) => {
    const { user } = useAuth();
    const isAuthenticated = !!user; // Derive from user presence
    const [connectionState, setConnectionState] = useState<WsState>(WS_STATES.DISCONNECTED);
    const [lastError, setLastError] = useState<string | null>(null);

    // Refs for WebSocket management
    const wsRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const reconnectIntervalRef = useRef<number>(WS_CONFIG.reconnectInterval);
    const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const listenersRef = useRef<Map<string, Set<WsEventListener>>>(new Map());
    // Reference counting for room subscriptions: Map<room, count>
    // Multiple hooks can subscribe to the same room independently.
    // Only sends actual subscribe/unsubscribe to server on 0↔1 transitions.
    const subscribedRoomsRef = useRef<Map<string, number>>(new Map());
    const shouldReconnectRef = useRef(true);
    // F3 fix: Track reconnection attempts to prevent infinite reconnection
    const reconnectAttemptsRef = useRef(0);

    /**
     * Get the WebSocket URL (handles both HTTP and HTTPS).
     */
    const getWsUrl = useCallback((): string => {
        // If URL is already absolute, use it directly
        if (WS_CONFIG.url.startsWith("ws://") || WS_CONFIG.url.startsWith("wss://")) {
            return WS_CONFIG.url;
        }
        // Otherwise build URL from current location
        const protocol = globalThis.location.protocol === "https:" ? "wss:" : "ws:";
        const host = globalThis.location.host;
        return `${protocol}//${host}${WS_CONFIG.url}`;
    }, []);

    /**
     * Send a message through the WebSocket.
     */
    const sendMessage = useCallback((message: WsMessage): boolean => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(message));
            return true;
        }
        return false;
    }, []);

    /**
     * Handle incoming WebSocket messages.
     */
    const handleMessage = useCallback((event: MessageEvent) => {
        try {
            const message = JSON.parse(event.data) as WsMessage;
            const eventType = message.type;

            // Dispatch to listeners
            if (eventType) {
                const listeners = listenersRef.current.get(eventType);
                if (listeners) {
                    listeners.forEach((callback) => {
                        try {
                            callback(message);
                        } catch (err) {
                            console.error("Error in WebSocket event listener:", err);
                        }
                    });
                }
            }

            // Also dispatch to wildcard listeners
            const wildcardListeners = listenersRef.current.get("*");
            if (wildcardListeners) {
                wildcardListeners.forEach((callback) => {
                    try {
                        callback(message);
                    } catch (err) {
                        console.error("Error in WebSocket wildcard listener:", err);
                    }
                });
            }
        } catch (err) {
            console.error("Error parsing WebSocket message:", err);
        }
    }, []);

    /**
     * Start ping interval to keep connection alive.
     */
    const startPing = useCallback(() => {
        if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
        }
        pingIntervalRef.current = setInterval(() => {
            sendMessage({ action: "ping" });
        }, WS_CONFIG.pingInterval);
    }, [sendMessage]);

    /**
     * Stop ping interval.
     */
    const stopPing = useCallback(() => {
        if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
        }
    }, []);

    /**
     * Re-subscribe to all previously subscribed rooms after reconnect.
     * Iterates over Map keys (rooms with count > 0).
     */
    const resubscribeRooms = useCallback(() => {
        subscribedRoomsRef.current.forEach((count, room) => {
            if (count > 0) {
                sendMessage({ action: "subscribe", room });
            }
        });
    }, [sendMessage]);

    /**
     * Connect to WebSocket server.
     */
    const connect = useCallback((): void => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            return;
        }

        setConnectionState(WS_STATES.CONNECTING);
        const url = getWsUrl();

        try {
            wsRef.current = new WebSocket(url);

            wsRef.current.onopen = () => {
                if (process.env.NODE_ENV === "development") {
                    console.log("[WS] Connected");
                }
                setConnectionState(WS_STATES.CONNECTED);
                setLastError(null);
                reconnectIntervalRef.current = WS_CONFIG.reconnectInterval;
                // F3 fix: Reset reconnection counter on successful connection
                reconnectAttemptsRef.current = 0;
                startPing();
                resubscribeRooms();
            };

            wsRef.current.onclose = (event) => {
                if (process.env.NODE_ENV === "development") {
                    console.log("[WS] Closed:", event.code, event.reason || "");
                }
                stopPing();

                // Attempt reconnect if not intentionally closed
                if (shouldReconnectRef.current && isAuthenticated) {
                    // F3 fix: Check max reconnection attempts
                    if (reconnectAttemptsRef.current >= WS_CONFIG.maxReconnectAttempts) {
                        console.error("Max WebSocket reconnection attempts reached");
                        setLastError("Impossible de se reconnecter. Veuillez rafraîchir la page.");
                        setConnectionState(WS_STATES.DISCONNECTED);
                        return;
                    }
                    reconnectAttemptsRef.current++;

                    // F2 fix: Skip DISCONNECTED state and go directly to RECONNECTING
                    // This avoids unnecessary re-renders and visual flicker
                    setConnectionState(WS_STATES.RECONNECTING);
                    reconnectTimeoutRef.current = setTimeout(() => {
                        reconnectIntervalRef.current = Math.min(
                            reconnectIntervalRef.current * WS_CONFIG.reconnectDecay,
                            WS_CONFIG.maxReconnectInterval,
                        );
                        connect();
                    }, reconnectIntervalRef.current);
                } else {
                    setConnectionState(WS_STATES.DISCONNECTED);
                }
            };

            wsRef.current.onerror = (error) => {
                if (process.env.NODE_ENV === "development") {
                    console.error("[WS] Error:", error);
                }
                setLastError("Connection error");
            };

            wsRef.current.onmessage = handleMessage;
        } catch (err) {
            console.error("Error creating WebSocket:", err);
            setLastError(err instanceof Error ? err.message : String(err));
            setConnectionState(WS_STATES.DISCONNECTED);
        }
    }, [getWsUrl, handleMessage, isAuthenticated, startPing, stopPing, resubscribeRooms]);

    /**
     * Disconnect from WebSocket server.
     */
    const disconnect = useCallback(() => {
        shouldReconnectRef.current = false;

        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        stopPing();

        if (wsRef.current) {
            // Detach the handlers before closing. The close event is delivered
            // asynchronously, so on unmount `onclose` would still run and call
            // setConnectionState on a component that is already gone — harmless in a
            // browser, but in jsdom it lands after the environment is torn down and
            // surfaces as "window is not defined".
            wsRef.current.onopen = null;
            wsRef.current.onclose = null;
            wsRef.current.onerror = null;
            wsRef.current.onmessage = null;
            wsRef.current.close();
            wsRef.current = null;
        }

        setConnectionState(WS_STATES.DISCONNECTED);
    }, [stopPing]);

    /**
     * Subscribe to a room (e.g., "operation:123").
     *
     * Uses reference counting: multiple hooks can subscribe to the same room.
     * Only sends actual WebSocket subscribe message when count goes from 0 to 1.
     *
     * @returns True if message sent, false if already subscribed or WS not connected.
     */
    const subscribe = useCallback(
        (room: string): boolean => {
            const currentCount = subscribedRoomsRef.current.get(room) || 0;
            subscribedRoomsRef.current.set(room, currentCount + 1);

            // Only send subscribe message on first subscriber (0 → 1)
            if (currentCount === 0) {
                return sendMessage({ action: "subscribe", room });
            }
            return true; // Already subscribed, no message needed
        },
        [sendMessage],
    );

    /**
     * Unsubscribe from a room.
     *
     * Uses reference counting: only sends actual WebSocket unsubscribe message
     * when count goes from 1 to 0 (last subscriber leaving).
     */
    const unsubscribe = useCallback(
        (room: string): void => {
            const currentCount = subscribedRoomsRef.current.get(room) || 0;

            if (currentCount <= 1) {
                // Last subscriber leaving → actually unsubscribe
                subscribedRoomsRef.current.delete(room);
                sendMessage({ action: "unsubscribe", room });
            } else {
                // Other hooks still need this room
                subscribedRoomsRef.current.set(room, currentCount - 1);
            }
        },
        [sendMessage],
    );

    /**
     * Add an event listener for a specific event type.
     *
     * @param eventType The event type to listen for (e.g., "pdf.progress"). Use "*" for all events.
     * @param callback The callback function to invoke.
     * @returns Cleanup function to remove the listener.
     */
    const addEventListener = useCallback(
        (eventType: string, callback: WsEventListener): (() => void) => {
            if (!listenersRef.current.has(eventType)) {
                listenersRef.current.set(eventType, new Set());
            }
            listenersRef.current.get(eventType)!.add(callback);

            // Return cleanup function
            return () => {
                const listeners = listenersRef.current.get(eventType);
                if (listeners) {
                    listeners.delete(callback);
                    if (listeners.size === 0) {
                        listenersRef.current.delete(eventType);
                    }
                }
            };
        },
        [],
    );

    // Connect when authenticated, disconnect when not
    useEffect(() => {
        if (isAuthenticated) {
            shouldReconnectRef.current = true;
            connect();
        } else {
            disconnect();
        }

        return () => {
            disconnect();
        };
    }, [isAuthenticated, connect, disconnect]);

    // F1 fix: Memoize context value to prevent unnecessary re-renders of consumers
    // Only re-create when actual values change
    // F9 fix: Removed sendMessage from export - only used internally
    const contextValue = useMemo<WebSocketContextValue>(
        () => ({
            // State
            connectionState,
            isConnected: connectionState === WS_STATES.CONNECTED,
            isReconnecting: connectionState === WS_STATES.RECONNECTING,
            lastError,

            // Methods
            subscribe,
            unsubscribe,
            addEventListener,

            // Constants
            WS_STATES,
        }),
        [connectionState, lastError, subscribe, unsubscribe, addEventListener],
    );

    return (
        <WebSocketContext.Provider value={contextValue}>
            {children}
        </WebSocketContext.Provider>
    );
};

/**
 * Hook to access the WebSocket context.
 */
export const useWebSocket = (): WebSocketContextValue => {
    const context = useContext(WebSocketContext);
    if (!context) {
        throw new Error("useWebSocket must be used within a WebSocketProvider");
    }
    return context;
};

/**
 * Hook to subscribe to a room and automatically unsubscribe on unmount.
 *
 * F4 fix: Separates subscription intent from transport state.
 * - When component mounts with a room: adds to subscribedRoomsRef (intent)
 * - When component unmounts: removes from subscribedRoomsRef
 * - When WS connects/reconnects: resubscribeRooms() handles re-sending messages
 * - When WS disconnects: intent is preserved, no unsubscribe sent
 *
 * This prevents race conditions where the room was removed from the ref
 * during disconnection, causing resubscribeRooms() to miss it.
 *
 * @param room The room to subscribe to (e.g., "operation:123"). Null disables subscription.
 */
export const useRoomSubscription = (room: string | null): void => {
    const { subscribe, unsubscribe, isConnected } = useWebSocket();
    const roomRef = useRef<string | null>(room);

    // Track the current room for cleanup
    useEffect(() => {
        roomRef.current = room;
    }, [room]);

    // Handle subscription when room or connection changes
    useEffect(() => {
        if (room && isConnected) {
            subscribe(room);
        }
    }, [room, isConnected, subscribe]);

    // Cleanup only on unmount or room change - NOT on disconnect
    useEffect(() => {
        return () => {
            // Only unsubscribe if we had a room (component unmounting or room changing)
            if (roomRef.current) {
                unsubscribe(roomRef.current);
            }
        };
    }, [room, unsubscribe]);
};

/**
 * Hook to listen to a specific WebSocket event type.
 *
 * @param eventType The event type to listen for. Pass null/empty to disable.
 * @param callback The callback to invoke when event is received.
 */
export const useWebSocketEvent = (
    eventType: string | null,
    callback: WsEventListener | null,
): void => {
    const { addEventListener } = useWebSocket();

    useEffect(() => {
        if (eventType && callback) {
            return addEventListener(eventType, callback);
        }
    }, [eventType, callback, addEventListener]);
};
